import { useEffect, useRef } from 'react';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Redo2,
  Strikethrough,
  Underline,
  Undo2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { investmentCellRichExtensions } from '@/editor/investmentCellRichExtensions';

interface ProductDescriptionEditorProps {
  id: string;
  value: string;
  onChange: (html: string) => void;
}

export default function ProductDescriptionEditor({
  id,
  value,
  onChange,
}: ProductDescriptionEditorProps) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const editor = useEditor({
    immediatelyRender: false,
    extensions: investmentCellRichExtensions,
    content: value,
    onUpdate: ({ editor: current }) =>
      onChangeRef.current(current.isEmpty ? '' : current.getHTML()),
    editorProps: {
      attributes: {
        id,
        role: 'textbox',
        'aria-label': 'Omschrijving in de offerte',
        'aria-multiline': 'true',
        class: 'min-h-32 px-3 py-2 text-sm focus:outline-none',
      },
    },
  });
  const formatting = useEditorState({
    editor,
    selector: ({ editor: current }) => ({
      bold: current?.isActive('bold') ?? false,
      italic: current?.isActive('italic') ?? false,
      underline: current?.isActive('underline') ?? false,
      bulletList: current?.isActive('bulletList') ?? false,
      orderedList: current?.isActive('orderedList') ?? false,
      canUndo: current?.can().undo() ?? false,
      canRedo: current?.can().redo() ?? false,
    }),
  });

  useEffect(() => {
    if (!editor || editor.isFocused) return;
    if ((editor.isEmpty ? '' : editor.getHTML()) !== value) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [editor, value]);

  const actions = [
    {
      label: 'Vet',
      icon: Bold,
      active: formatting?.bold,
      run: () => editor?.chain().focus().toggleBold().run(),
    },
    {
      label: 'Cursief',
      icon: Italic,
      active: formatting?.italic,
      run: () => editor?.chain().focus().toggleItalic().run(),
    },
    {
      label: 'Onderstrepen',
      icon: Underline,
      active: formatting?.underline,
      run: () => editor?.chain().focus().toggleUnderline().run(),
    },
    {
      label: 'Doorhalen',
      icon: Strikethrough,
      active: editor?.isActive('strike'),
      run: () => editor?.chain().focus().toggleStrike().run(),
    },
    {
      label: 'Opsomming',
      icon: List,
      active: formatting?.bulletList,
      run: () => editor?.chain().focus().toggleBulletList().run(),
    },
    {
      label: 'Genummerde lijst',
      icon: ListOrdered,
      active: formatting?.orderedList,
      run: () => editor?.chain().focus().toggleOrderedList().run(),
    },
  ];

  return (
    <div className="overflow-hidden rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
      <div
        role="group"
        aria-label="Tekstopmaak"
        className="flex flex-wrap items-center gap-1 border-b bg-muted/30 p-1.5"
      >
        {actions.map(({ label, icon: Icon, active, run }) => (
          <Button
            key={label}
            type="button"
            variant={active ? 'secondary' : 'ghost'}
            size="icon-sm"
            aria-label={label}
            title={label}
            aria-pressed={Boolean(active)}
            disabled={!editor}
            onMouseDown={(event) => event.preventDefault()}
            onClick={run}
          >
            <Icon className="h-4 w-4" />
          </Button>
        ))}
        <div className="mx-1 h-5 border-l" />
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label="Ongedaan maken"
          title="Ongedaan maken"
          disabled={!formatting?.canUndo}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => editor?.chain().focus().undo().run()}
        >
          <Undo2 className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label="Opnieuw uitvoeren"
          title="Opnieuw uitvoeren"
          disabled={!formatting?.canRedo}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => editor?.chain().focus().redo().run()}
        >
          <Redo2 className="h-4 w-4" />
        </Button>
      </div>
      <div className="[&_.ProseMirror_p]:mb-2 [&_.ProseMirror_p:last-child]:mb-0 [&_.ProseMirror_ul]:mb-2 [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:pl-6 [&_.ProseMirror_ol]:mb-2 [&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:pl-6 [&_.ProseMirror_li_p]:mb-0">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
